import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  EntitlementStatus,
  SubscriptionAction,
  UserSubscriptionStatus,
} from 'src/app/shared/enums/subscription';
import { KEYS } from 'src/app/shared/utils/constants/storage.constants';
import {
  AhaUserType,
  getDateOrdinal,
} from 'src/app/shared/utils/SubscriptionUtil';
import { LocalStorageService } from '../../storage/local-storage.service';
import { AccountProfileService } from '../account/account-profile.service';
import { GetEntitlementsResponseModel } from './model/entitlement/entitlement.response.model';

@Injectable({
  providedIn: 'root',
})
@Injectable()
export class UserEntitlementService {
  ahaUserType: AhaUserType = AhaUserType.ANONYMOUS;
  entitlementRes: GetEntitlementsResponseModel | null = null;
  planActionBtn$ = new BehaviorSubject<SubscriptionAction | null>(
    SubscriptionAction.SUBSCRIBE
  );

  constructor(
    private readonly accountProfileService: AccountProfileService,
    private readonly localStorage: LocalStorageService
  ) {}

  get subscription() {
    return (
      (this.entitlementRes?.AccountServiceMessage?.length &&
        this.entitlementRes?.AccountServiceMessage[0]) ||
      null
    );
  }

  get subscriptionNameDetailed() {
    return this.ahaUserType !== AhaUserType.REGISTERED && this.subscription
      ? `${this.subscription.serviceName} - ${this.subscription.description}`
      : 'no_active_plan';
  }

  get subscriptionName() {
    return this.ahaUserType !== AhaUserType.REGISTERED && this.subscription
      ? this.subscription.serviceName
      : '';
  }

  get validityEndDate() {
    return this.subscription?.validityEndDate
      ? getDateOrdinal(new Date(this.subscription.validityEndDate), false)
      : '';
  }

  get isSubscribed() {
    return Boolean(this.subscription);
  }

  get subscriptionUrn() {
    return this.subscription?.name || null;
  }

  userSubscriptionStatus(): UserSubscriptionStatus {
    if (!this.localStorage.getItem(KEYS.EVERGENT_ACCESS_TOKEN)?.length) {
      return UserSubscriptionStatus.ANONYMOUS;
    }
    const account =
      this.accountProfileService.account?.GetAccountProfileResponseMessage;

    const subscription = this.subscription;
    const isSubscribed =
      Boolean(account?.subscriptionStatus) && Boolean(subscription);

    const isUpgradable =
      isSubscribed &&
      subscription?.upgradable &&
      subscription?.status &&
      subscription?.status.toLowerCase() !== EntitlementStatus.FINAL_BILL;

    const goldPackageActive =
      subscription?.name?.includes('urn:package:combo') &&
      subscription?.status.toLowerCase() !== EntitlementStatus.FINAL_BILL;

    const cancelledSubscription =
      subscription?.isContent == false &&
      subscription?.status.toLowerCase() == EntitlementStatus.FINAL_BILL;

    const isPartnerSubscription =
      !goldPackageActive && !subscription?.upgradable && isSubscribed;

    if (goldPackageActive) {
      return UserSubscriptionStatus.GOLD_SUBSCRIBED;
    } else if (isUpgradable || isPartnerSubscription) {
      return UserSubscriptionStatus.NEEDS_UPGRADE;
    } else if (!isSubscribed) {
      return UserSubscriptionStatus.NOT_SUBSCRIBED;
    } else if (cancelledSubscription) {
      return UserSubscriptionStatus.CANCELLED;
    } else {
      return UserSubscriptionStatus.ANONYMOUS;
    }
  }

  bannerSubscriptionStatus(): UserSubscriptionStatus {
    const userSubscriptionStatus = this.userSubscriptionStatus();
    switch (userSubscriptionStatus) {
      case UserSubscriptionStatus.ANONYMOUS:
        return UserSubscriptionStatus.ANONYMOUS;

      case UserSubscriptionStatus.NEEDS_UPGRADE:
        return UserSubscriptionStatus.SUBSCRIBED;

      case UserSubscriptionStatus.NOT_SUBSCRIBED:
        if (this.isChurnedUser()) {
          return UserSubscriptionStatus.CHURNED;
        } else {
          return UserSubscriptionStatus.REGISTERED;
        }

      case UserSubscriptionStatus.CANCELLED:
      default:
        return null;
    }
  }

  setSubscriptionAction(): SubscriptionAction | null {
    const status = this.userSubscriptionStatus();
    let action: SubscriptionAction | null = null;
    if (
      status === UserSubscriptionStatus.ANONYMOUS ||
      status === UserSubscriptionStatus.NOT_SUBSCRIBED
    ) {
      action = SubscriptionAction.SUBSCRIBE;
    } else if (status === UserSubscriptionStatus.NEEDS_UPGRADE) {
      action = SubscriptionAction.UPGRADE;
    } else if (status === UserSubscriptionStatus.CANCELLED) {
      action = SubscriptionAction.SUBSCRIBE;
    } else {
      action = null;
    }
    this.planActionBtn$.next(action);
    return action;
  }

  isChurnedUser(): boolean {
    return (
      this.planActionBtn$.getValue() === SubscriptionAction.SUBSCRIBE &&
      !this.accountProfileService.account.GetAccountProfileResponseMessage
        .neverSubscribed
    );
  }
}
